import type { AttachmentValidationError } from "./types";

/**
 * Mime types the SOLA chat will accept, with the same per-type max size
 * (in bytes) the Laravel server enforces. Keep this table in sync with
 * `App\Http\Requests\Sola\UploadFileRequest::allowList()`.
 */
export const SOLA_ATTACHMENT_LIMITS: Record<string, number> = {
  // images — 10 MB
  "image/png": 10 * 1024 * 1024,
  "image/jpeg": 10 * 1024 * 1024,
  "image/webp": 10 * 1024 * 1024,
  "image/gif": 10 * 1024 * 1024,
  // pdf — 32 MB
  "application/pdf": 32 * 1024 * 1024,
  // word — 25 MB
  "application/msword": 25 * 1024 * 1024,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": 25 * 1024 * 1024,
  // text — 2 MB
  "text/plain": 2 * 1024 * 1024,
  "text/markdown": 2 * 1024 * 1024,
  "text/csv": 2 * 1024 * 1024,
  "application/json": 2 * 1024 * 1024,
};

export const SOLA_ACCEPT_ATTRIBUTE = Object.keys(SOLA_ATTACHMENT_LIMITS).join(",");

/** Browsers don't always set `type` on dropped files — sniff by extension as a fallback. */
const EXTENSION_FALLBACK: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  gif: "image/gif",
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  txt: "text/plain",
  md: "text/markdown",
  markdown: "text/markdown",
  csv: "text/csv",
  json: "application/json",
};

export function resolveMimeType(file: { name: string; type: string }): string {
  if (file.type && SOLA_ATTACHMENT_LIMITS[file.type] !== undefined) {
    return file.type;
  }
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext && EXTENSION_FALLBACK[ext]) {
    return EXTENSION_FALLBACK[ext];
  }
  return file.type || "application/octet-stream";
}

export function describeMime(mime: string): string {
  switch (mime) {
    case "image/png":
    case "image/jpeg":
    case "image/webp":
    case "image/gif":
      return "image";
    case "application/pdf":
      return "PDF";
    case "application/msword":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return "document";
    case "text/plain":
    case "text/markdown":
    case "text/csv":
    case "application/json":
      return "text file";
    default:
      return "file";
  }
}

export function validateAttachment(file: File): AttachmentValidationError | null {
  const mime = resolveMimeType(file);
  const limit = SOLA_ATTACHMENT_LIMITS[mime];
  if (!limit) {
    return {
      filename: file.name,
      reason: `${describeMime(mime)} (${mime || "unknown"}) is not supported.`,
    };
  }
  if (file.size > limit) {
    const limitMb = (limit / (1024 * 1024)).toFixed(0);
    return {
      filename: file.name,
      reason: `Larger than the ${limitMb} MB limit for ${describeMime(mime)}.`,
    };
  }
  if (file.size === 0) {
    return { filename: file.name, reason: "File is empty." };
  }
  return null;
}

export function partitionFiles(files: File[]): {
  ok: File[];
  rejected: AttachmentValidationError[];
} {
  const ok: File[] = [];
  const rejected: AttachmentValidationError[] = [];
  for (const f of files) {
    const error = validateAttachment(f);
    if (error) {
      rejected.push(error);
    } else {
      ok.push(f);
    }
  }
  return { ok, rejected };
}
